import { useState } from 'react';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  DATE_RANGE_PRESETS,
  PRESET_LABELS,
  formatDateRange,
  fromInputValue,
  resolveDateRange,
  toInputValue,
  type DateRange,
  type DateRangePreset,
} from './date-range';

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  align?: 'start' | 'end';
  id?: string;
  className?: string;
}

/** Period picker. Presets first, because that is what people pick 95 % of the time. */
export function DateRangePicker({
  value,
  onChange,
  align = 'start',
  id,
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(() => ({
    from: toInputValue(value.from),
    to: toInputValue(value.to),
  }));

  const pick = (preset: DateRangePreset) => {
    if (preset === 'personalizado') {
      setCustom({ from: toInputValue(value.from), to: toInputValue(value.to) });
      onChange({ ...value, preset });
      return;
    }
    onChange(resolveDateRange(preset));
    setOpen(false);
  };

  const applyCustom = (next: { from: string; to: string }) => {
    setCustom(next);
    const from = fromInputValue(next.from, 'from');
    const to = fromInputValue(next.to, 'to');
    if (from && to && from <= to) onChange({ from, to, preset: 'personalizado' });
  };

  const invalid = (() => {
    const from = fromInputValue(custom.from, 'from');
    const to = fromInputValue(custom.to, 'to');
    return value.preset === 'personalizado' && (!from || !to || from > to);
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button id={id} variant="outline" size="sm" className={cn('gap-1.5', className)}>
          <CalendarDays className="size-3.5 text-muted-foreground" aria-hidden />
          <span>{formatDateRange(value)}</span>
          <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden />
        </Button>
      </PopoverTrigger>

      <PopoverContent align={align} className="w-64 p-1">
        <div role="radiogroup" aria-label="Período">
          {DATE_RANGE_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              role="radio"
              aria-checked={value.preset === preset}
              onClick={() => pick(preset)}
              className={cn(
                'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm transition-colors duration-150 ease-out hover:bg-muted',
                value.preset === preset && 'bg-muted font-medium',
              )}
            >
              {PRESET_LABELS[preset]}
            </button>
          ))}
        </div>

        {value.preset === 'personalizado' && (
          <div className="mt-1 space-y-2 border-t border-border p-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="range-from" className="text-xs text-muted-foreground">
                  Desde
                </Label>
                <Input
                  id="range-from"
                  type="date"
                  value={custom.from}
                  max={custom.to}
                  onChange={(event) => applyCustom({ ...custom, from: event.target.value })}
                  className="h-7"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="range-to" className="text-xs text-muted-foreground">
                  Hasta
                </Label>
                <Input
                  id="range-to"
                  type="date"
                  value={custom.to}
                  min={custom.from}
                  onChange={(event) => applyCustom({ ...custom, to: event.target.value })}
                  className="h-7"
                />
              </div>
            </div>

            <p role="status" className={cn('text-xs', invalid ? 'text-destructive' : 'sr-only')}>
              {invalid ? 'Revisa las fechas: el inicio debe ser anterior al término.' : 'Rango válido'}
            </p>

            <Button size="sm" className="w-full" disabled={invalid} onClick={() => setOpen(false)}>
              Aplicar
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
